import type { RecipeSummary } from "./api";
import type { Layout, RecipeSortKey } from "./layout/layout";

/**
 * Sorting, filtering and grouping the recipe list, the same for every view (Tyler, 2026-10-01): what the
 * View, Sort and Filter menus on the Recipes page do, kept apart from the page so it can be tested.
 */

type Recipes = Layout["recipes"];

/** Whether a recipe could be brewed today, as far as its linked lines say. */
export type StockState = "ready" | "short" | "unlinked" | "none";

export function stockState(stock: RecipeSummary["stock"]): StockState {
  if (stock.lines === 0) return "none";
  if (stock.short > 0) return "short";
  if (stock.linked < stock.lines) return "unlinked";
  return "ready";
}

const sortValue: Record<RecipeSortKey, (r: RecipeSummary) => string | number | null> = {
  name: (r) => r.name,
  style: (r) => r.style?.name ?? null,
  batch: (r) => Number(r.batchLiters),
  og: (r) => r.estimates.og,
  fg: (r) => r.estimates.fg,
  abv: (r) => r.estimates.abv,
  ibu: (r) => r.estimates.ibu,
  srm: (r) => r.estimates.srm,
  lastBrewed: (r) => r.lastBrewedOn,
  updated: (r) => r.updatedAt,
};

const byName = (a: RecipeSummary, b: RecipeSummary) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });

/**
 * Sorted by one figure either way, then by name. A recipe without the figure (never brewed, no style,
 * no yeast for a final gravity) goes last whichever way it runs, rather than counting as the lowest.
 */
export function sortRecipes(list: RecipeSummary[], sort: Recipes["sort"]): RecipeSummary[] {
  const get = sortValue[sort.key];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    if (x === null || y === null) {
      if (x === y) return byName(a, b);
      return x === null ? 1 : -1;
    }
    const c =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), undefined, { sensitivity: "base", numeric: true });
    return c !== 0 ? c * sign : byName(a, b);
  });
}

export function filterRecipes(
  list: RecipeSummary[],
  filters: Pick<Recipes, "type" | "style" | "stock">,
): RecipeSummary[] {
  return list.filter(
    (r) =>
      (filters.type === null || r.type === filters.type) &&
      (filters.style === null || r.style?.name === filters.style) &&
      (filters.stock === "all" || stockState(r.stock) === filters.stock),
  );
}

/** Every style the recipes have, for the filter. */
export function styleNames(list: RecipeSummary[]): string[] {
  return [...new Set(list.flatMap((r) => (r.style ? [r.style.name] : [])))].sort((a, b) =>
    a.localeCompare(b),
  );
}

type Style = NonNullable<RecipeSummary["style"]>;

const rangesIn = (s: Style) => [s.og, s.fg, s.abv, s.ibu, s.srm].filter(Boolean).length;

/**
 * The styles the brewery's recipes are written to, one per name, in name order, each with the most ranges
 * any recipe gives it: what the builder's Style field offers, so a style picked there brings its ranges
 * (Scott, 2026-10-02). A style list of our own waits on the BJCP's permission (PLAN.md, "Styles").
 */
export function knownStyles(list: RecipeSummary[]): Style[] {
  const byName = new Map<string, Style>();
  for (const r of list) {
    if (!r.style?.name.trim()) continue;
    const key = r.style.name.trim().toLowerCase();
    const have = byName.get(key);
    if (!have || rangesIn(r.style) > rangesIn(have))
      byName.set(key, { ...r.style, name: r.style.name.trim() });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** The known style with this name, ignoring case and spaces at either end. */
export const styleNamed = (styles: Style[], name: string): Style | undefined =>
  styles.find((s) => s.name.trim().toLowerCase() === name.trim().toLowerCase());

export interface StyleGroup {
  /** The style's name, or null for recipes without one. */
  style: string | null;
  recipes: RecipeSummary[];
}

/** By style, styles in name order and those without one last, each keeping the order it was given. */
export function groupByStyle(list: RecipeSummary[]): StyleGroup[] {
  const groups = new Map<string | null, RecipeSummary[]>();
  for (const r of list) {
    const key = r.style?.name ?? null;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }
  return [...groups.entries()]
    .map(([style, recipes]) => ({ style, recipes }))
    .sort((a, b) => {
      if (a.style === null || b.style === null) return a.style === null ? 1 : -1;
      return a.style.localeCompare(b.style);
    });
}
